'use client';

import { useLayoutEffect, useRef } from 'react';
import { useAuth } from '@clerk/nextjs';
import { usePathname } from 'next/navigation';
import { ConditionalNavbar } from '@/components/layout/conditional-navbar';
import { CustomAppChrome } from '@/components/layout/custom-app-chrome';
import { MarketingDesktopOnlyGate } from '@/components/layout/marketing-desktop-only-gate';
import { NavAccentLoadingProvider } from '@/components/layout/nav-accent-loading-context';
import { AppShellProvider, useAppShell } from '@/components/layout/app-shell-context';
import { isMainLandingRoute } from '@/lib/navigation/is-main-landing-route';
import { cn } from '@/lib/utils';

function isShellExcludedPath(pathname: string | null): boolean {
  if (!pathname) return true;
  if (pathname.startsWith('/sign-in') || pathname.startsWith('/sign-up') || pathname.startsWith('/accept-invitation')) {
    return true;
  }
  if (pathname.match(/^\/[^/]+\/(demo-chat|chat)/)) return true;
  if (
    pathname === '/onboarding' ||
    pathname === '/onboarding-v2' ||
    pathname === '/onboarding-v3' ||
    pathname === '/post-sign'
  ) {
    return true;
  }
  if (pathname.startsWith('/create-agent')) return true;
  if (pathname.startsWith('/marketing')) return true;
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return true;
  return false;
}

const SAAS_SHELL_HEIGHT_CLASS =
  'h-dvh min-h-0 w-full supports-[height:100dvh]:h-[100dvh]';

function isImmersiveAppRoute(pathname: string | null): boolean {
  if (!pathname) return false;
  return (
    pathname === '/test-agent' ||
    pathname.startsWith('/test-agent/') ||
    pathname === '/chats' ||
    pathname.startsWith('/chats/') ||
    pathname === '/onboarding-v3' ||
    pathname === '/admin/support/settings/skills' ||
    pathname === '/marketing/record' ||
    pathname.startsWith('/marketing/record/')
  );
}

/** SaaS top-nav shell: navbar stays fixed; only main scrolls. */
function SaasTopNavChrome({
  banners,
  children,
  mainScrollRef,
  shellHeightClass = SAAS_SHELL_HEIGHT_CLASS,
  immersiveRoute = false,
}: {
  banners?: React.ReactNode;
  children: React.ReactNode;
  mainScrollRef?: React.RefObject<HTMLDivElement | null>;
  shellHeightClass?: string;
  immersiveRoute?: boolean;
}) {
  return (
    <NavAccentLoadingProvider>
      <div className={cn('flex min-h-0 flex-col overflow-hidden', shellHeightClass)}>
        <div className="shrink-0">
          <ConditionalNavbar />
        </div>
        <main
          ref={mainScrollRef}
          className={cn(
            'min-h-0 flex-1 overscroll-y-contain [-webkit-overflow-scrolling:touch]',
            immersiveRoute
              ? 'flex flex-col overflow-hidden'
              : 'overflow-y-auto overflow-x-hidden',
          )}
        >
          {banners}
          {children}
        </main>
      </div>
    </NavAccentLoadingProvider>
  );
}

function AppShellResolved({
  children,
  banners,
}: {
  children: React.ReactNode;
  banners: React.ReactNode;
}) {
  const pathname = usePathname();
  const { variant } = useAppShell();
  const useCustomShell = variant === 'custom' && !isMainLandingRoute(pathname);
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const immersiveRoute = isImmersiveAppRoute(pathname);

  useLayoutEffect(() => {
    if (useCustomShell) return;
    mainScrollRef.current?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname, useCustomShell]);

  if (useCustomShell) {
    return (
      <CustomAppChrome banners={banners}>
        <MarketingDesktopOnlyGate>{children}</MarketingDesktopOnlyGate>
      </CustomAppChrome>
    );
  }

  return (
    <SaasTopNavChrome
      banners={banners}
      mainScrollRef={mainScrollRef}
      immersiveRoute={immersiveRoute}
    >
      <MarketingDesktopOnlyGate>{children}</MarketingDesktopOnlyGate>
    </SaasTopNavChrome>
  );
}

function DefaultChrome({
  children,
  banners,
}: {
  children: React.ReactNode;
  banners: React.ReactNode;
}) {
  const pathname = usePathname();
  return (
    <SaasTopNavChrome banners={banners} immersiveRoute={isImmersiveAppRoute(pathname)}>
      {children}
    </SaasTopNavChrome>
  );
}

export function AppShell({
  children,
  banners,
}: {
  children: React.ReactNode;
  banners: React.ReactNode;
}) {
  const pathname = usePathname();
  const { isSignedIn, isLoaded } = useAuth();
  const excluded = isShellExcludedPath(pathname);

  // Public FAQ stays chrome-less; signed-in FAQ uses the normal app shell once Clerk loads.
  if (pathname === '/faq' && (!isLoaded || !isSignedIn)) {
    return <>{children}</>;
  }

  if (excluded) {
    return <DefaultChrome banners={banners}>{children}</DefaultChrome>;
  }

  // Landing page (`/`): always DefaultChrome regardless of auth state.
  // MinimalProductShell calls AppShell for `/`; signed-in users are redirected by middleware.
  // Keeping DefaultChrome here avoids the AppShellProvider mounting on `/` and then
  // remounting page animations (hero, typing teaser) when Clerk flips isLoaded.
  if (isMainLandingRoute(pathname)) {
    return <DefaultChrome banners={banners}>{children}</DefaultChrome>;
  }

  // Explicitly signed-out on a product route (isLoaded=true, isSignedIn=false):
  // middleware would normally redirect, but guard here for safety.
  if (isLoaded && !isSignedIn) {
    return <DefaultChrome banners={banners}>{children}</DefaultChrome>;
  }

  // Product routes: always render AppShellProvider — even while Clerk is still loading
  // (isLoaded=false). This is critical to prevent a server/client hydration mismatch:
  //
  //   Server:          isLoaded=true  → AppShellProvider > AppShellResolved
  //   Client (initial):isLoaded=false → AppShellProvider > AppShellResolved  (SAME)
  //
  // Previously the client rendered DefaultChrome while isLoaded=false, then switched to
  // AppShellProvider once Clerk loaded. React tried to reconcile two completely different
  // component trees, causing "Failed to execute 'insertBefore' on 'Node'" DOM crashes on
  // every product page load, predominantly on mobile where Clerk loads more slowly.
  //
  // AppShellProvider already handles !isLoaded gracefully (defaults to variant: 'saas').
  return (
    <AppShellProvider>
      <AppShellResolved banners={banners}>{children}</AppShellResolved>
    </AppShellProvider>
  );
}
